import { useState, useRef, useEffect, useCallback } from 'react';
import { useGlobalContext } from '../context/GlobalContext';
import { debugAuth, mockUsers, isDebugMode } from '../services/debugService';
import { Button } from './index';

/**
 * 🐞 The Ladybug Oracle — Draggable Debug Companion
 *
 * A tiny crimson beetle of cosmic wisdom, wandering wherever your cursor
 * dares to drag her. Click her spots to reveal the sacred mock-user scrolls.
 * She's development-only, she's draggable, she's FABULOUS. ✨
 * Drag her across the canvas like a true digital familiar. 🎭
 */
export default function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, setUser } = useGlobalContext();

  // 🐛 Initial perch: top-right corner, like a ladybug on a windowsill
  const [pos, setPos] = useState({ x: window.innerWidth - 64, y: 16 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const btnRef = useRef(null);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    hasDragged.current = true;
    setPos({
      x: Math.min(Math.max(0, e.clientX - dragOffset.current.x), window.innerWidth - 48),
      y: Math.min(Math.max(0, e.clientY - dragOffset.current.y), window.innerHeight - 48),
    });
  }, []);

  const onMouseUp = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  if (!isDebugMode) return null;

  const handleMouseDown = (e) => {
    const rect = btnRef.current.getBoundingClientRect();
    dragging.current = true;
    hasDragged.current = false;
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  };

  const handleClick = () => {
    // 🐞 Only toggle panel if this was a click, not the end of a drag
    if (!hasDragged.current) setIsOpen((o) => !o);
  };

  const handleLogin = (userType) => {
    const success = debugAuth.loginUser(userType);
    if (success) {
      setUser(mockUsers[userType]);
      window.location.reload();
    }
  };

  const handleLogout = () => {
    debugAuth.logout();
    setUser(null);
    window.location.reload();
  };

  const isDebugSession = debugAuth.isDebugSession();

  // 🗺️ Panel opens to the left if near right edge, above if near bottom edge
  const panelRight = pos.x > window.innerWidth / 2;
  const panelBelow = pos.y < window.innerHeight / 2;

  return (
    <div
      style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999 }}
    >
      {/* 🐞 The Ladybug Button */}
      <button
        ref={btnRef}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        title="Debug Panel — drag me!"
        style={{ cursor: dragging.current ? 'grabbing' : 'grab' }}
        className="w-11 h-11 rounded-full shadow-xl flex items-center justify-center text-2xl select-none
          bg-gradient-to-br from-red-500 to-red-700 hover:from-red-400 hover:to-red-600
          border-2 border-red-900/30 transition-transform hover:scale-110 active:scale-95"
      >
        🐞
      </button>

      {/* 🔮 Debug Panel Popup */}
      {isOpen && (
        <div
          className="absolute bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl p-4 w-72"
          style={{
            [panelRight ? 'right' : 'left']: 0,
            [panelBelow ? 'top' : 'bottom']: '52px',
          }}
        >
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xl">🐞</span>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                Debug Panel
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Dev mode only
              </p>
            </div>
          </div>

          {/* Current Status */}
          <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
              Status
            </p>
            {isDebugSession ? (
              <div className="text-green-600 dark:text-green-400 space-y-0.5">
                <p className="text-xs">✅ Debug Session Active</p>
                <p className="text-xs">User: {user?.name || 'Unknown'}</p>
                <p className="text-xs">Member: {user?.is_member ? 'Yes' : 'No'}</p>
              </div>
            ) : (
              <p className="text-xs text-red-500 dark:text-red-400">❌ No Session</p>
            )}
          </div>

          {/* Mock Login Options */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              Mock Users:
            </p>
            <Button onClick={() => handleLogin('member')} className="w-full text-xs bg-green-500 hover:bg-green-600">
              Login as Member (John Doe)
            </Button>
            <Button onClick={() => handleLogin('firstTimeUser')} className="w-full text-xs bg-blue-500 hover:bg-blue-600">
              Login as First-Time User (Jane Smith)
            </Button>
            <Button onClick={() => handleLogin('nonMember')} className="w-full text-xs bg-yellow-500 hover:bg-yellow-600">
              Login as Non-Member (Bob Wilson)
            </Button>
            {isDebugSession && (
              <Button onClick={handleLogout} className="w-full text-xs bg-red-500 hover:bg-red-600">
                Logout (Clear Session)
              </Button>
            )}
          </div>

          <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
              {localStorage.getItem('sessionId')?.substring(0, 24) ?? 'no session'}…
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
